import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { exchangeSession } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

// Simple in-memory de-dup across component mounts (handles back/forward edge case)
const seenSessionIds = new Set();

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);

    // CRITICAL: strip the session_id from URL *immediately*, before any async work,
    // so a refresh/back-navigation can't re-fire with a now-consumed token.
    const cleanPath = window.location.pathname || "/";
    window.history.replaceState({}, "", cleanPath);

    if (!match) {
      navigate("/", { replace: true });
      return;
    }
    const session_id = match[1];

    // Guard: if this exact session_id was already attempted this tab session,
    // bail silently (it's one-time-use).
    if (seenSessionIds.has(session_id)) {
      navigate("/", { replace: true });
      return;
    }
    seenSessionIds.add(session_id);

    (async () => {
      try {
        const data = await exchangeSession(session_id);
        if (!data?.user) throw new Error("No user in session response");
        setUser(data.user);
        if (!data.user.age_verified || !data.user.region) {
          navigate("/welcome", { replace: true, state: { user: data.user } });
        } else {
          navigate("/dashboard", { replace: true, state: { user: data.user } });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[AuthCallback] session exchange failed:", e);
        // Silent redirect — no alarming toast. Most failures are harmless
        // (reused session_id on refresh/back-nav). User can simply click Sign-in again.
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen grid place-items-center" data-testid="auth-callback">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="w-6 h-6 animate-spin text-white/70" />
        <p className="text-sm text-white/60">Warming up your Fanpuff season…</p>
      </div>
    </div>
  );
}
